// ── Shared vector-text PDF generator for Quotation / Invoice / Receipt ──────
// Replaces the old html2pdf.js/html2canvas approach (which rasterizes the
// letterhead into a screenshot, then embeds that screenshot as an image
// inside the PDF — the cause of blurry text, jagged borders on small boxes,
// and 4-6x larger file sizes) and the old window.print() approach (genuine
// text, but needs the user to click "Save as PDF" in a print dialog).
//
// pdfmake builds a real vector-text PDF entirely in the browser and can
// trigger a silent, one-click download — no server dependency (works on
// Hostinger shared hosting same as localhost) and no print dialog.
//
// Trade-off accepted deliberately: pdfmake ships its own bundled font
// (Roboto) rather than the firm's custom Google Font choice from Billing
// Settings — embedding arbitrary web fonts into pdfmake requires converting
// real .ttf files into its virtual filesystem, which is a much bigger lift
// for a purely cosmetic font swap. Brand color, logo, bank details, QR code
// and signature are still fully per-firm.

import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';

// pdfmake's vfs_fonts export shape changed between major versions — 0.1/0.2
// nested it as { pdfMake: { vfs: {...} } }, 0.3+ exports the filename→base64
// map directly. Handle both so this doesn't silently break on an upgrade.
pdfMake.vfs = pdfFonts.pdfMake ? pdfFonts.pdfMake.vfs : pdfFonts;

const GRAY = '#888888';
const HAIRLINE = '#e8e8e8';
const DARK = '#1a1a1a';
const DISCOUNT_COLOR = '#e8502a';

const inr = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';

export function numberToWords(num) {
  if (!num || isNaN(num)) return '';
  const a = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const inWords = (n) => {
    if (n < 20) return a[n];
    if (n < 100) return b[Math.floor(n / 10)] + (n % 10 ? ' ' + a[n % 10] : '');
    if (n < 1000) return a[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + inWords(n % 100) : '');
    if (n < 100000) return inWords(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + inWords(n % 1000) : '');
    if (n < 10000000) return inWords(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 ? ' ' + inWords(n % 100000) : '');
    return inWords(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 ? ' ' + inWords(n % 10000000) : '');
  };
  return inWords(parseInt(num)) + ' Rupees only.';
}

// pdfmake's `image` node only knows how to decode JPEG and PNG — anything
// else (SVG, WEBP, GIF, BMP — all pickable via Billing Settings' generic
// `accept="image/*"` file input) makes pdfmake throw "Invalid image: Unknown
// image format" and abort the ENTIRE PDF, logo/signature or not. Re-encoding
// through an offscreen canvas normalizes any format the browser can decode
// into a PNG pdfmake accepts. Fails soft (resolves null) on anything canvas
// can't handle either, so a broken asset just omits the image.
function normalizeToPngDataUrl(dataUrl) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        if (!canvas.width || !canvas.height) { resolve(null); return; }
        canvas.getContext('2d').drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } catch (e) {
        resolve(null); // canvas tainted (cross-origin) or decode failure
      }
    };
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });
}

// Fetches a remote image (logo/signature/QR) and converts it to a base64
// data URI — pdfmake's `image` nodes need a data URI or a pre-registered vfs
// name, they can't load a bare remote URL directly. Fails soft (returns
// null) so a broken/CORS-blocked asset just omits the image instead of
// breaking the whole PDF.
export async function imageUrlToDataUrl(url) {
  if (!url) return null;
  try {
    let dataUrl = url;
    if (!url.startsWith('data:')) {
      const res = await fetch(url, { mode: 'cors' });
      if (!res.ok) return null;
      const blob = await res.blob();
      dataUrl = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      });
      if (!dataUrl) return null;
    }
    const mime = (dataUrl.match(/^data:([^;]+);/) || [])[1] || '';
    if (mime === 'image/png' || mime === 'image/jpeg' || mime === 'image/jpg') {
      return dataUrl;
    }
    return await normalizeToPngDataUrl(dataUrl);
  } catch (e) {
    return null;
  }
}

// pdfmake 0.3+'s .download() is async (a Promise), unlike older versions'
// callback style — awaited here so a rendering failure actually rejects and
// reaches the caller's try/catch instead of becoming a silent unhandled
// promise rejection.
export async function downloadPdfDoc(docDefinition, filename) {
  await pdfMake.createPdf(docDefinition).download(filename);
}

// ── Shared header block (logo/firm name+address on the left, doc title +
// meta rows on the right) — used by all three document types. ─────────────
function buildHeader({ docTitle, metaRows, firmName, firmAddr, firmMobile, firmEmail, logoDataUrl, pColor }) {
  const left = [];
  if (logoDataUrl) {
    left.push({ image: logoDataUrl, width: 140, margin: [0, 0, 0, 6] });
  }
  left.push({ text: firmName || 'ChanakyaX', style: 'firmName', color: pColor });
  if (firmAddr) left.push({ text: firmAddr, style: 'small', margin: [0, 2, 0, 0] });
  const contactLine = [firmMobile ? 'Mobile: ' + firmMobile : '', firmEmail || ''].filter(Boolean).join('  |  ');
  if (contactLine) left.push({ text: contactLine, style: 'small' });

  const right = [{ text: docTitle, style: 'docTitle', color: pColor, alignment: 'right' }];
  metaRows.forEach(([label, value]) => {
    if (!value) return;
    right.push({
      alignment: 'right',
      margin: [0, 2, 0, 0],
      text: [{ text: label + ' ', color: GRAY, fontSize: 9 }, { text: String(value), bold: true, fontSize: 9 }],
    });
  });

  return {
    columns: [{ width: '*', stack: left }, { width: 'auto', stack: right }],
    margin: [0, 0, 0, 10],
  };
}

function bankAndQrColumn({ bankName, bankAccount, bankIfsc, bankBranch, upiId, qrDataUrl, pColor }) {
  if (!bankName && !bankAccount && !upiId) return { text: '' };
  const rows = [{ text: 'BANK DETAILS', style: 'label', margin: [0, 0, 0, 4] }];
  const row = (label, val) => val && rows.push({ columns: [{ text: label, width: 55, color: GRAY, fontSize: 9 }, { text: val, fontSize: 9 }] });
  row('Name:', bankName);
  row('Account:', bankAccount);
  row('IFSC:', bankIfsc);
  row('Bank:', bankBranch);
  if (qrDataUrl && upiId) {
    rows.push({
      margin: [0, 8, 0, 0],
      columns: [
        { image: qrDataUrl, width: 56 },
        { width: 10, text: '' },
        { width: 'auto', stack: [
          { text: 'UPI ID', bold: true, color: pColor, fontSize: 9 },
          { text: upiId, fontSize: 9 },
        ] },
      ],
    });
  }
  return { stack: rows };
}

function signatureBlock({ signatureDataUrl, signatureName, firmName }) {
  const stack = [];
  if (signatureDataUrl) stack.push({ image: signatureDataUrl, width: 90, alignment: 'center', margin: [0, 0, 0, 4] });
  else stack.push({ canvas: [{ type: 'line', x1: 0, y1: 0, x2: 100, y2: 0, lineWidth: 0.5, lineColor: '#999' }], margin: [30, 20, 0, 4] });
  stack.push({ text: signatureName || 'Authorised Signature', bold: true, fontSize: 10, alignment: 'center' });
  stack.push({ text: 'For ' + (firmName || ''), fontSize: 9, color: GRAY, alignment: 'center', margin: [0, 1, 0, 0] });
  return { stack, width: 150 };
}

const sharedStyles = {
  firmName: { fontSize: 14, bold: true },
  docTitle: { fontSize: 22, bold: true },
  small: { fontSize: 9, color: '#555555' },
  label: { fontSize: 9, bold: true, color: GRAY },
  tableHeader: { fontSize: 9, bold: true, color: GRAY },
};

const sharedDefaults = { pageSize: 'A4', pageMargins: [36, 36, 36, 36], defaultStyle: { fontSize: 10, color: DARK }, styles: sharedStyles };

// ── QUOTATION ────────────────────────────────────────────────────────────
export function buildQuotationDoc({ quoteNo, date, validTill, pColor, firmName, firmAddr, firmMobile, firmEmail, logoDataUrl, clientName, contactName, clientPhone, clientEmail, clientGstin, leadRef, items, subtotal, discountTotal, total, notes, terms, bankName, bankAccount, bankIfsc, bankBranch, upiId, qrDataUrl, signatureDataUrl, signatureName }) {
  const header = buildHeader({
    docTitle: 'Quotation', pColor, firmName, firmAddr, firmMobile, firmEmail, logoDataUrl,
    metaRows: [['Quote No.', quoteNo], ['Date', fmtDate(date)], ['Valid Till', fmtDate(validTill)]],
  });

  const tableBody = [[
    { text: '#', style: 'tableHeader' }, { text: 'Service / Description', style: 'tableHeader' },
    { text: 'Amount', style: 'tableHeader', alignment: 'right' }, { text: 'Discount', style: 'tableHeader', alignment: 'right' },
    { text: 'Net', style: 'tableHeader', alignment: 'right' },
  ]];
  items.forEach((it, i) => {
    tableBody.push([
      { text: String(i + 1), fontSize: 9 },
      { stack: [{ text: it.particular, bold: true, color: pColor, fontSize: 10 }].concat(it.description ? [{ text: it.description, fontSize: 8.5, color: GRAY, margin: [0, 1, 0, 0] }] : []) },
      { text: inr(it.amount), alignment: 'right', fontSize: 9 },
      { text: it.discount > 0 ? '−' + inr(it.discount) : '—', alignment: 'right', fontSize: 9, color: it.discount > 0 ? DISCOUNT_COLOR : GRAY },
      { text: inr(it.net), alignment: 'right', bold: true, fontSize: 9 },
    ]);
  });

  const totalsRows = [{ columns: [{ text: 'Subtotal', color: GRAY, fontSize: 9 }, { text: inr(subtotal), alignment: 'right', fontSize: 9 }] }];
  if (discountTotal > 0) totalsRows.push({ margin: [0, 3, 0, 0], columns: [{ text: 'Discount', color: DISCOUNT_COLOR, fontSize: 9 }, { text: '−' + inr(discountTotal), alignment: 'right', fontSize: 9, color: DISCOUNT_COLOR }] });
  totalsRows.push({ margin: [0, 6, 0, 0], columns: [{ text: 'Total', bold: true, fontSize: 12, color: pColor }, { text: inr(total), alignment: 'right', bold: true, fontSize: 12, color: pColor }] });

  return {
    ...sharedDefaults,
    content: [
      header,
      { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 523, y2: 0, lineWidth: 1.5, lineColor: pColor }], margin: [0, 0, 0, 10] },
      {
        columns: [
          { width: '*', stack: [
            { text: 'PREPARED FOR', style: 'label', margin: [0, 0, 0, 4] },
            { text: clientName, bold: true, fontSize: 12 },
            { text: [contactName && contactName !== clientName ? 'Contact: ' + contactName : '', clientPhone ? '\nPhone: ' + clientPhone : '', clientEmail ? '\nEmail: ' + clientEmail : '', clientGstin ? '\nGSTIN: ' + clientGstin : ''].join(''), style: 'small', margin: [0, 2, 0, 0] },
          ] },
          leadRef ? { width: 'auto', text: 'Lead Ref: ' + leadRef, style: 'small', alignment: 'right' } : { width: 'auto', text: '' },
        ],
        margin: [0, 0, 0, 14],
      },
      { table: { headerRows: 1, widths: ['auto', '*', 60, 55, 65], body: tableBody }, layout: hairlineLayout(pColor) },
      { columns: [
        bankAndQrColumn({ bankName, bankAccount, bankIfsc, bankBranch, upiId, qrDataUrl, pColor }),
        { width: 200, stack: totalsRows },
      ], margin: [0, 14, 0, 0] },
      total > 0 ? { text: [{ text: 'Amount in Words: ', bold: true, color: GRAY, fontSize: 9 }, { text: numberToWords(total), italics: true, fontSize: 9 }], margin: [0, 14, 0, 0] } : {},
      notes ? { text: [{ text: 'Notes: ', bold: true, color: GRAY, fontSize: 9 }, { text: notes, fontSize: 9 }], margin: [0, 8, 0, 0] } : {},
      { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 523, y2: 0, lineWidth: 0.5, lineColor: HAIRLINE }], margin: [0, 16, 0, 10] },
      { columns: [
        terms ? { width: '*', stack: [{ text: 'Terms & Conditions', bold: true, fontSize: 9, margin: [0, 0, 0, 4] }, { text: terms, fontSize: 8.5, color: '#555555', lineHeight: 1.4 }] } : { width: '*', text: '' },
        signatureBlock({ signatureDataUrl, signatureName, firmName }),
      ] },
    ],
  };
}

// ── INVOICE ──────────────────────────────────────────────────────────────
export function buildInvoiceDoc({ docTitle, invoiceNo, date, dueDate, pColor, firmName, firmAddr, firmMobile, firmEmail, firmGstin, firmPan, logoDataUrl, clientName, clientAddr, clientMobile, clientGstin, items, showSac, isGst, gstType, subtotal, discountTotal, cgst, sgst, igst, roundOff, total, amountPaid, balanceDue, showLedger, amountInWords, terms, bankName, bankAccount, bankIfsc, bankBranch, upiId, qrDataUrl, signatureDataUrl, signatureName }) {
  const header = buildHeader({
    docTitle: docTitle || 'Invoice', pColor, firmName, firmAddr, firmMobile, firmEmail, logoDataUrl,
    metaRows: [['Invoice No.', invoiceNo], ['Invoice Date', fmtDate(date)], ['Due Date', fmtDate(dueDate)]],
  });

  const widths = ['auto', '*'];
  const headerRow = [{ text: '#', style: 'tableHeader' }, { text: 'Description', style: 'tableHeader' }];
  if (showSac) { widths.push(45); headerRow.push({ text: 'SAC', style: 'tableHeader', alignment: 'center' }); }
  widths.push(60); headerRow.push({ text: 'Rate', style: 'tableHeader', alignment: 'right' });
  widths.push(55); headerRow.push({ text: 'Discount', style: 'tableHeader', alignment: 'right' });
  if (isGst) { widths.push(38); headerRow.push({ text: 'GST%', style: 'tableHeader', alignment: 'center' }); }
  widths.push(65); headerRow.push({ text: 'Amount', style: 'tableHeader', alignment: 'right' });
  const tableBody = [headerRow];
  items.forEach((it, i) => {
    const row = [
      { text: String(i + 1), fontSize: 9 },
      { stack: [{ text: it.particular || it.name, bold: true, color: pColor, fontSize: 10 }].concat(it.description ? [{ text: it.description, fontSize: 8.5, color: GRAY, margin: [0, 1, 0, 0] }] : []) },
    ];
    if (showSac) row.push({ text: it.sac_code || '—', alignment: 'center', fontSize: 9 });
    row.push({ text: inr(it.rate), alignment: 'right', fontSize: 9 });
    row.push({ text: Number(it.discount_amount || 0) > 0 ? inr(it.discount_amount) : '—', alignment: 'right', fontSize: 9, color: Number(it.discount_amount || 0) > 0 ? DISCOUNT_COLOR : GRAY });
    if (isGst) row.push({ text: '18%', alignment: 'center', fontSize: 9 });
    row.push({ text: inr(it.net_amount), alignment: 'right', bold: true, fontSize: 9 });
    tableBody.push(row);
  });

  const totalsRows = [{ columns: [{ text: 'Subtotal', color: GRAY, fontSize: 9 }, { text: inr(subtotal), alignment: 'right', fontSize: 9 }] }];
  if (discountTotal > 0) totalsRows.push({ margin: [0, 3, 0, 0], columns: [{ text: 'Discount', color: DISCOUNT_COLOR, fontSize: 9 }, { text: '−' + inr(discountTotal), alignment: 'right', fontSize: 9, color: DISCOUNT_COLOR }] });
  if (isGst && gstType === 'cgst_sgst') {
    totalsRows.push({ margin: [0, 3, 0, 0], columns: [{ text: 'CGST (9%)', color: GRAY, fontSize: 9 }, { text: inr(cgst), alignment: 'right', fontSize: 9 }] });
    totalsRows.push({ margin: [0, 3, 0, 0], columns: [{ text: 'SGST (9%)', color: GRAY, fontSize: 9 }, { text: inr(sgst), alignment: 'right', fontSize: 9 }] });
  } else if (isGst && gstType === 'igst') {
    totalsRows.push({ margin: [0, 3, 0, 0], columns: [{ text: 'IGST (18%)', color: GRAY, fontSize: 9 }, { text: inr(igst), alignment: 'right', fontSize: 9 }] });
  }
  if (Number(roundOff) !== 0) totalsRows.push({ margin: [0, 3, 0, 0], columns: [{ text: 'Round Off', color: GRAY, fontSize: 8.5 }, { text: (roundOff > 0 ? '+' : '') + Number(roundOff).toFixed(2), alignment: 'right', fontSize: 8.5 }] });
  totalsRows.push({ margin: [0, 6, 0, 0], columns: [{ text: 'Total', bold: true, fontSize: 12, color: pColor }, { text: inr(total), alignment: 'right', bold: true, fontSize: 12, color: pColor }] });
  if (showLedger) {
    if (Number(amountPaid) > 0) totalsRows.push({ margin: [0, 8, 0, 0], columns: [{ text: 'Received', color: GRAY, fontSize: 9 }, { text: inr(amountPaid), alignment: 'right', fontSize: 9, color: '#16a34a', bold: true }] });
    totalsRows.push({ margin: [0, 3, 0, 0], columns: [{ text: 'Balance Due', bold: true, fontSize: 9, color: '#dc2626' }, { text: inr(balanceDue), alignment: 'right', bold: true, fontSize: 9, color: '#dc2626' }] });
  }

  return {
    ...sharedDefaults,
    content: [
      header,
      { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 523, y2: 0, lineWidth: 1.5, lineColor: pColor }], margin: [0, 0, 0, 10] },
      {
        columns: [
          { width: '*', stack: [
            { text: 'BILL TO', style: 'label', margin: [0, 0, 0, 4] },
            { text: clientName, bold: true, fontSize: 12 },
            { text: [clientAddr || '', clientMobile ? '\nMobile: ' + clientMobile : '', clientGstin ? '\nGSTIN: ' + clientGstin : ''].join(''), style: 'small', margin: [0, 2, 0, 0] },
          ] },
          (firmGstin || firmPan) ? { width: 'auto', alignment: 'right', stack: [
            { text: 'YOUR FIRM DETAILS', style: 'label', margin: [0, 0, 0, 4] },
            firmGstin ? { text: [{ text: 'GSTIN: ', color: GRAY }, { text: firmGstin, bold: true }], fontSize: 9 } : {},
            firmPan ? { text: [{ text: 'PAN: ', color: GRAY }, { text: firmPan, bold: true }], fontSize: 9 } : {},
          ] } : { width: 'auto', text: '' },
        ],
        margin: [0, 0, 0, 14],
      },
      { table: { headerRows: 1, widths, body: tableBody }, layout: hairlineLayout(pColor) },
      { columns: [
        bankAndQrColumn({ bankName, bankAccount, bankIfsc, bankBranch, upiId, qrDataUrl, pColor }),
        { width: 200, stack: totalsRows },
      ], margin: [0, 14, 0, 0] },
      amountInWords ? { text: [{ text: 'Amount in Words: ', bold: true, color: GRAY, fontSize: 9 }, { text: amountInWords, italics: true, fontSize: 9 }], margin: [0, 14, 0, 0] } : {},
      { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 523, y2: 0, lineWidth: 0.5, lineColor: HAIRLINE }], margin: [0, 16, 0, 10] },
      { columns: [
        terms ? { width: '*', stack: [{ text: 'Terms & Conditions', bold: true, fontSize: 9, margin: [0, 0, 0, 4] }, { text: terms, fontSize: 8.5, color: '#555555', lineHeight: 1.4 }] } : { width: '*', text: '' },
        signatureBlock({ signatureDataUrl, signatureName, firmName }),
      ] },
    ],
  };
}

// ── RECEIPT ──────────────────────────────────────────────────────────────
export function buildReceiptDoc({ receiptNo, date, referenceNumber, pColor, firmName, firmAddr, firmMobile, firmEmail, logoDataUrl, clientName, clientAddr, mode, amount, amountInWords, remark, settlements, balanceAfter, showLedger, terms, signatureDataUrl, signatureName }) {
  const header = buildHeader({
    docTitle: 'Receipt', pColor, firmName, firmAddr, firmMobile, firmEmail, logoDataUrl,
    metaRows: [['Receipt No.', receiptNo], ['Receipt Date', fmtDate(date)], ['Ref / UTR', referenceNumber]],
  });

  const content = [
    header,
    { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 523, y2: 0, lineWidth: 1.5, lineColor: pColor }], margin: [0, 0, 0, 10] },
    { stack: [
      { text: 'RECEIVED FROM', style: 'label', margin: [0, 0, 0, 4] },
      { text: clientName, bold: true, fontSize: 12 },
      clientAddr ? { text: clientAddr, style: 'small', margin: [0, 2, 0, 0] } : {},
      remark ? { text: 'Remark: ' + remark, style: 'small', italics: true, margin: [0, 4, 0, 0] } : {},
    ], margin: [0, 0, 0, 14] },
    { table: { widths: ['*', 'auto'], body: [[
      { stack: [
        { text: 'Payment received through ' + (mode || 'Bank Transfer'), bold: true, color: pColor, fontSize: 10 },
        { text: 'Payment Mode: ' + (mode || 'Bank Transfer'), fontSize: 8.5, color: GRAY, margin: [0, 1, 0, 0] },
      ] },
      { text: inr(amount), bold: true, fontSize: 11, alignment: 'right', margin: [0, 4, 0, 0] },
    ]] }, layout: hairlineLayout(pColor) },
    { columns: [
      { width: '*', text: [{ text: 'Amount in Words: ', bold: true, color: GRAY, fontSize: 9 }, { text: amountInWords || numberToWords(amount), italics: true, fontSize: 9 }] },
      { width: 'auto', text: [{ text: 'Total: ', color: GRAY, fontSize: 10 }, { text: inr(amount), bold: true, fontSize: 13, color: pColor }], alignment: 'right' },
    ], margin: [0, 10, 0, 14] },
  ];

  if (Array.isArray(settlements) && settlements.length) {
    content.push({ text: 'Payment settled to following', bold: true, fontSize: 9, margin: [0, 0, 0, 6] });
    const body = [[{ text: 'Date', style: 'tableHeader' }, { text: 'Invoice No.', style: 'tableHeader' }, { text: 'Invoice Amount', style: 'tableHeader', alignment: 'right' }, { text: 'Payment', style: 'tableHeader', alignment: 'right' }]];
    settlements.forEach(s => body.push([
      { text: fmtDate(s.date), fontSize: 9 }, { text: s.invoiceNo, bold: true, color: pColor, fontSize: 9 },
      { text: inr(s.invoiceAmount), alignment: 'right', fontSize: 9 }, { text: inr(s.payment), alignment: 'right', bold: true, fontSize: 9, color: '#16a34a' },
    ]));
    content.push({ table: { headerRows: 1, widths: ['auto', 'auto', '*', '*'], body }, layout: hairlineLayout(pColor), margin: [0, 0, 0, 14] });
  }

  if (showLedger) {
    content.push({ columns: [
      { text: 'Balance after this payment:', color: GRAY, fontSize: 10 },
      { text: balanceAfter <= 0 ? 'NIL — Fully Paid' : inr(balanceAfter), bold: true, fontSize: 10, alignment: 'right', color: balanceAfter <= 0 ? '#16a34a' : '#dc2626' },
    ], margin: [0, 0, 0, 14] });
  }

  content.push({ canvas: [{ type: 'line', x1: 0, y1: 0, x2: 523, y2: 0, lineWidth: 0.5, lineColor: HAIRLINE }], margin: [0, 8, 0, 10] });
  content.push({ columns: [
    terms ? { width: '*', stack: [{ text: terms, fontSize: 8.5, color: '#555555', lineHeight: 1.4 }] } : { width: '*', text: '' },
    signatureBlock({ signatureDataUrl, signatureName, firmName }),
  ] });

  return { ...sharedDefaults, content };
}

// A hairline table layout — thin gray lines only, no cell fills — matching
// the clean reference style, in the firm's brand color for the header rule.
function hairlineLayout(pColor) {
  return {
    hLineWidth: (i, node) => (i === 0 || i === 1) ? 1.5 : 0.5,
    vLineWidth: () => 0,
    hLineColor: (i) => (i === 0 || i === 1) ? pColor : HAIRLINE,
    paddingLeft: () => 6, paddingRight: () => 6, paddingTop: () => 6, paddingBottom: () => 6,
  };
}
